import { useContext, useEffect, useRef } from "react";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import { forEachFrame, readFrame } from "./parseFrame";
import { ChartBar, RawCandle, toChartBar } from "../api/candles/useCandles";

const candlesChannel = "candles";

/**
 * The two payload shapes the `candles` channel sends.
 *
 * A snapshot arrives once on subscribe, wrapped; every live update is a bare
 * bar. Modelled as a discriminated union so an unhandled shape is a type error
 * rather than a message that silently fails a `typeof` guard and is dropped
 * with nothing logged.
 */
type CandleSnapshotPayload = { type: "snapshot"; candles: RawCandle[] };
type CandlePayload = RawCandle | CandleSnapshotPayload;

const isSnapshot = (payload: CandlePayload): payload is CandleSnapshotPayload =>
  (payload as CandleSnapshotPayload).type === "snapshot";

/**
 * Live bars for one market and resolution.
 *
 * The backend consumes the indexer's Redis candle stream, which the indexer
 * publishes on every fill for all nine of its native intervals, and republishes
 * it as `candles:{instrument}:{resolution}`. It is push-only: there is no
 * polling timer behind this channel, because a bar only moves when a trade
 * happens and the trade already arrives as an event.
 *
 * A message is the whole bucket as of that fill, not a delta — two fills in one
 * bar produce two messages with the same `time` and a growing volume. The
 * consumer replaces by `time`; it must never accumulate.
 *
 * Releases its channel in the effect cleanup, on unmount and when the market
 * or resolution changes. Subscriptions are reference counted, so another
 * chart on the same channel keeps it alive.
 */
const useCandlesWSS = (
  instrumentName: string | undefined,
  resolution: string,
  onBar: (bar: ChartBar) => void
) => {
  const { triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);

  // Held in a ref so a new inline callback on every render does not re-run the
  // message effect (and re-deliver bars already applied).
  const onBarRef = useRef(onBar);
  useEffect(() => {
    onBarRef.current = onBar;
  }, [onBar]);

  useEffect(() => {
    if (!instrumentName || !resolution) return undefined;
    const channels = [`${candlesChannel}:${instrumentName}:${resolution}`];
    triggerSubscribe("subscribe", channels);
    return () => triggerUnsubscribe(channels);
  }, [instrumentName, resolution, triggerSubscribe, triggerUnsubscribe]);

  useEffect(() => {
    if (!lastMessages || !instrumentName) return;

    const expected = `${candlesChannel}:${instrumentName}:${resolution}`;
    const expectedLower = expected.toLowerCase();

    forEachFrame(lastMessages, "useCandlesWSS", (message) => {
      const parsed = readFrame<{ channel?: string; data?: CandlePayload }>(message);
      if (!parsed) return;

      const { channel, data } = parsed;
      if (typeof channel !== "string" || !data) return;
      if (channel !== expected && channel.toLowerCase() !== expectedLower) {
        return;
      }

      // The snapshot sent on subscribe is handled by the REST load, which has
      // paging; taking it here would duplicate that work and fight it.
      if (isSnapshot(data)) return;

      if (typeof data.time !== "number" || data.open === undefined) return;
      onBarRef.current(toChartBar(data));
    });
  }, [instrumentName, lastMessages, resolution]);
};

export default useCandlesWSS;
